import {createSlice} from '@reduxjs/toolkit';
import type {PayloadAction} from '@reduxjs/toolkit';
import {LOAD_LOCK_EXEMPT_MY_STUFF_BANNER_IDS, MY_STUFF_BANNER_PRIORITY} from '@Libraries/my-stuff/my-stuff-banners.types';
import type {MyStuffBannerId} from '@Libraries/my-stuff/my-stuff-banners.types';
import type {RootState} from '@/store';

interface MyStuffBannerCandidatePayload {
  bannerId: MyStuffBannerId;
  registrantKey: string;
}

interface MyStuffBannersState {
  candidateRegistrantKeys: Partial<Record<MyStuffBannerId, string[]>>;
  shownBannerId: MyStuffBannerId | null;
  isDismissedForLoad: boolean;
}

const initialState: MyStuffBannersState = {
  candidateRegistrantKeys: {},
  shownBannerId: null,
  isDismissedForLoad: false,
};

/**
 * The single-banner-per-load rule: the first MARKETING banner shown in a My Stuff load claims the
 * marketing slot for the whole load
 */
const getActiveBannerIdForState = (state: MyStuffBannersState): MyStuffBannerId | null => {
  const hasEligibleCandidate = (bannerId: MyStuffBannerId): boolean => {
    return (state.candidateRegistrantKeys[bannerId]?.length ?? 0) > 0;
  };

  const isLockedOutForLoad = (bannerId: MyStuffBannerId): boolean => {
    if (LOAD_LOCK_EXEMPT_MY_STUFF_BANNER_IDS.includes(bannerId)) {
      return false;
    }

    if (state.isDismissedForLoad) {
      return true;
    }

    return state.shownBannerId !== null && state.shownBannerId !== bannerId;
  };

  const activeBannerId = MY_STUFF_BANNER_PRIORITY.find((bannerId) => {
    return hasEligibleCandidate(bannerId) && !isLockedOutForLoad(bannerId);
  });

  return activeBannerId ?? null;
};

const myStuffBannersSlice = createSlice({
  name: 'myStuffBanners',
  initialState,
  reducers: {
    registerMyStuffBannerCandidate: (state, action: PayloadAction<MyStuffBannerCandidatePayload>): void => {
      const registrantKeys = state.candidateRegistrantKeys[action.payload.bannerId] ?? [];
      if (!registrantKeys.includes(action.payload.registrantKey)) {
        registrantKeys.push(action.payload.registrantKey);
      }
      state.candidateRegistrantKeys[action.payload.bannerId] = registrantKeys;
    },
    unregisterMyStuffBannerCandidate: (state, action: PayloadAction<MyStuffBannerCandidatePayload>): void => {
      const registrantKeys = state.candidateRegistrantKeys[action.payload.bannerId];
      if (!registrantKeys) {
        return;
      }
      state.candidateRegistrantKeys[action.payload.bannerId] = registrantKeys.filter((registrantKey) => {
        return registrantKey !== action.payload.registrantKey;
      });
    },
    /**
     * Dispatched when the user navigates between My Stuff pages, BEFORE the new page's candidate banners are reported
     */
    myStuffBannerPageChanged: (state): void => {
      if (state.shownBannerId !== null) {
        return;
      }

      const activeBannerId = getActiveBannerIdForState(state);
      if (activeBannerId !== null && !LOAD_LOCK_EXEMPT_MY_STUFF_BANNER_IDS.includes(activeBannerId)) {
        state.shownBannerId = activeBannerId;
      }
    },
    dismissMyStuffBannersForLoad: (state): void => {
      state.isDismissedForLoad = true;
    },
  },
});

export const {registerMyStuffBannerCandidate, unregisterMyStuffBannerCandidate, myStuffBannerPageChanged, dismissMyStuffBannersForLoad} = myStuffBannersSlice.actions;

/** The single banner allowed on screen right now. */
export const selectActiveMyStuffBannerId = (state: RootState): MyStuffBannerId | null => {
  return getActiveBannerIdForState(state.myStuffBanners);
};

export const myStuffBannersReducer = myStuffBannersSlice.reducer;
